import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { FieldTree, disabled, form, required } from '@angular/forms/signals';
import {
  DropdownComponent,
  DropdownOption,
} from '../dropdown/dropdown.component';
import { ExpandableTextComponent } from '../../expandable-text/expandable-text.component';
import type {
  ShipsanChapter,
  ShipsanItem,
  ShipsanItemType,
} from './shipsan-regulation-lookup.data';
import {
  pointsText,
  splitBreadcrumb,
  withoutNumberDash,
} from '../regulation-lookup-labels';

export interface ShipsanRegulationLookupValue {
  chapterId: string;
  chapterLabel: string;
  itemNo: string;
  itemLabel: string;
  section: string | null;
  subsection: string | null;
  breadcrumb: string;
  title: string;
  type: ShipsanItemType;
  typeLabel: string;
  pointValue: number;
  page: number;
  description: string;
}

type PickerValue = string | null;
interface PickerField {
  id: string;
  label: string;
  placeholder: string;
  options: DropdownOption[];
}

export const SHIPSAN_REGULATION_LOOKUP_PLACEHOLDER =
  'ShipSan requirement text and points will be shown here once a chapter and an item are selected.';

let chaptersLoad: Promise<ShipsanChapter[]> | null = null;
function loadShipsanChapters(): Promise<ShipsanChapter[]> {
  chaptersLoad ??= import('./shipsan-regulation-lookup.data').then(
    data => data.SHIPSAN_CHAPTERS
  );
  return chaptersLoad;
}

export function shipsanPointsChipClass(points: number): string {
  return points >= 3 ? 'chip-orange' : 'chip-warning';
}

export function shipsanItemOptionLabel(item: ShipsanItem): string {
  return `${withoutNumberDash(item.label)} · ${pointsText(item.pointValue)} · ${item.type}`;
}

export function shipsanItemGroup(item: ShipsanItem): string | undefined {
  if (!item.section) return undefined;
  return item.subsection
    ? `${item.section} / ${item.subsection}`
    : item.section;
}

@Component({
  selector: 'app-shipsan-regulation-lookup',
  imports: [DropdownComponent, ExpandableTextComponent],
  templateUrl: './shipsan-regulation-lookup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShipsanRegulationLookupComponent implements OnInit {
  readonly placeholderText = SHIPSAN_REGULATION_LOOKUP_PLACEHOLDER;
  readonly pointsText = pointsText;
  readonly pointsChipClass = shipsanPointsChipClass;
  readonly field =
    input.required<FieldTree<ShipsanRegulationLookupValue | null>>();
  readonly index = input(0);

  protected readonly state = computed(() => this.field()());
  private readonly idSuffix = computed(() =>
    this.index() ? `-${this.index() + 1}` : ''
  );
  private readonly chapters = signal<ShipsanChapter[]>([]);
  private readonly chapterValue = signal<PickerValue>(null);
  private readonly selectedChapter = computed(
    () => this.chapters().find(c => c.id === this.chapterValue()) ?? null
  );
  private readonly itemValue = linkedSignal<ShipsanChapter | null, PickerValue>(
    { source: this.selectedChapter, computation: () => null }
  );
  private readonly selectedItem = computed(
    () =>
      this.selectedChapter()?.items.find(i => i.itemNo === this.itemValue()) ??
      null
  );

  readonly chapterTree = form(this.chapterValue, path => {
    disabled(path, { when: () => this.state().disabled() });
    required(path, { when: () => this.state().required() });
  });
  readonly itemTree = form(this.itemValue, path => {
    disabled(path, {
      when: () => this.state().disabled() || !this.selectedChapter(),
    });
    required(path, { when: () => this.state().required() });
  });

  readonly chapterField = computed<PickerField>(() => ({
    id: `shipsan-chapter${this.idSuffix()}`,
    label: 'Manual chapter',
    placeholder: 'Select a chapter',
    options: this.chapters().map(chapter => ({
      label: chapter.label,
      value: chapter.id,
    })),
  }));
  readonly itemField = computed<PickerField>(() => ({
    id: `shipsan-item${this.idSuffix()}`,
    label: 'ShipSan item',
    placeholder: 'Select a ShipSan item',
    options: (this.selectedChapter()?.items ?? []).map(item => ({
      label: shipsanItemOptionLabel(item),
      value: item.itemNo,
      group: shipsanItemGroup(item),
    })),
  }));

  private readonly pickedValue = computed<ShipsanRegulationLookupValue | null>(
    () => {
      const chapter = this.selectedChapter();
      const item = this.selectedItem();
      if (!chapter || !item) return null;
      const path = [chapter.label, item.section, item.subsection].filter(
        (part): part is string => !!part
      );
      return {
        chapterId: chapter.id,
        chapterLabel: chapter.label,
        itemNo: item.itemNo,
        itemLabel: item.label,
        section: item.section,
        subsection: item.subsection,
        breadcrumb: [...path, `Item ${item.itemNo}`].join(' / '),
        title: item.title,
        type: item.type,
        typeLabel: item.typeLabel,
        pointValue: item.pointValue,
        page: item.page,
        description: item.description,
      };
    }
  );

  readonly compositeValue = linkedSignal<
    ShipsanRegulationLookupValue | null,
    ShipsanRegulationLookupValue | null
  >({ source: this.pickedValue, computation: value => value });

  readonly breadcrumbParts = computed(() =>
    splitBreadcrumb(this.compositeValue()?.breadcrumb ?? '')
  );

  constructor() {
    effect(() => {
      const value = this.compositeValue();
      untracked(() => {
        const state = this.state();
        if (sameShipsanSelection(state.value(), value)) return;
        state.value.set(value);
        state.markAsDirty();
      });
    });
    effect(() => {
      if (!this.state().touched()) return;
      untracked(() => {
        this.chapterTree().markAsTouched();
        this.itemTree().markAsTouched();
      });
    });
  }

  ngOnInit(): void {
    const value = this.state().value();
    if (value) this.compositeValue.set(value);
    void loadShipsanChapters().then(chapters => {
      this.chapters.set(chapters);
      if (value) this.restoreSelection(chapters, value.itemNo);
    });
  }

  private restoreSelection(chapters: ShipsanChapter[], itemNo: string): void {
    const chapter = chapters.find(c => c.items.some(i => i.itemNo === itemNo));
    if (!chapter) return;
    this.chapterValue.set(chapter.id);
    this.itemValue.set(itemNo);
  }
}

export function sameShipsanSelection(
  a: ShipsanRegulationLookupValue | null,
  b: ShipsanRegulationLookupValue | null
): boolean {
  return (a?.itemNo ?? null) === (b?.itemNo ?? null);
}
